import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  Clock,
  Users,
  Calendar,
  AlertTriangle,
  Search,
  Settings,
  Stethoscope,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import MainLayout from '../components/layout/MainLayout';
import { Button, Card, Input, Badge, Modal } from '../components/ui';
import type { BadgeVariant } from '../components/ui';
import { useCollection } from '../hooks/useFirestore';
import { useToast } from '../contexts/ToastContext';
import type { Sala, TipoSala, PrioridadSala } from '../types';
import styles from './ConfigSalasPage.module.css';

/* ── Constants ── */

const TIPOS_SALA: { value: TipoSala; label: string }[] = [
  { value: 'general', label: 'Medicina General' },
  { value: 'pediatria', label: 'Pediatría' },
  { value: 'urgencias', label: 'Urgencias' },
  { value: 'especialidades', label: 'Especialidades' },
  { value: 'psicologia', label: 'Psicología' },
  { value: 'ginecologia', label: 'Ginecología' },
  { value: 'dermatologia', label: 'Dermatología' },
  { value: 'otro', label: 'Otro' },
];

const PRIORIDADES: { value: PrioridadSala; label: string; variant: BadgeVariant }[] = [
  { value: 'normal', label: 'Normal', variant: 'success' },
  { value: 'alta', label: 'Alta', variant: 'warning' },
  { value: 'urgente', label: 'Urgente', variant: 'error' },
];

// DIAS_SEMANA removido — V2 muestra los horarios vía componente HorariosEditor.

/* ── Helpers ── */

/**
 * Estimación simple si la sala está abierta.
 *
 * NOTA: `sala.estado` se sincroniza con horarios via cron cada 1 min.
 * Si está `cerrada` pero los horarios dicen "abierta", probablemente el
 * cron aún no ha corrido. Para precisión real (lectura de subcolección
 * `horarios`) usar `IndicadoresSala` o `useReglasHorario`.
 *
 * Estados que indican apertura efectiva: 'abierta'.
 * Estados que indican cierre efectivo: 'cerrada', 'pausa', 'bloqueada', 'emergencia'.
 */
function isSalaOpen(sala: Sala): boolean {
  if (!sala.activa) return false;
  if (sala.estado === 'abierta') return true;
  // Si no hay estado pero la sala es activa, asumir abierta (cron sincronizará)
  if (!sala.estado) return true;
  return false;
}

// formatDias removido — los horarios ahora se ven en la tab "Horarios"
// vía HorariosEditor (subcolección V2).

/* ── Component ── */

export default function ConfigSalasPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data: salasData, loading } = useCollection<Sala>('salas');
  // `eliminarSala` es un soft-delete (preserva médicos/horarios para
  // auditoría) — sin este filtro la sala "eliminada" seguía apareciendo en la
  // lista sin cambio visible, dando la impresión de que "Eliminar" no hacía
  // nada.
  const salas = useMemo(() => (salasData ?? []).filter((s) => !s.eliminadaEn), [salasData]);

  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState<{ id: string; nombre: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return salas;
    const term = searchTerm.toLowerCase();
    return salas.filter(
      (s) =>
        s.nombre.toLowerCase().includes(term) ||
        s.descripcion.toLowerCase().includes(term),
    );
  }, [salas, searchTerm]);

  const handleToggleActiva = useCallback(
    async (sala: Sala) => {
      try {
        const actualizarSala = httpsCallable(functions, 'portal_actualizarSala');
        await actualizarSala({ salaId: sala.id, activa: !sala.activa });
        addToast('success', `Sala "${sala.nombre}" ${sala.activa ? 'desactivada' : 'activada'}`);
      } catch {
        addToast('error', 'Error al cambiar estado');
      }
    },
    [addToast],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      const eliminarSala = httpsCallable(functions, 'portal_eliminarSala');
      await eliminarSala({ salaId: deleteModal.id });
      addToast('success', `Sala "${deleteModal.nombre}" eliminada`);
      setDeleteModal(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar la sala';
      addToast('error', msg);
    }
    setDeleting(false);
  }, [deleteModal, addToast]);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const total = salas.length;
    const activas = salas.filter((s) => s.activa).length;
    const abiertas = salas.filter(isSalaOpen).length;
    return { total, activas, abiertas };
  }, [salas]);

  /* ── Render ── */

  return (
    <MainLayout pageTitle="Configuración de salas">
      {/* KPI Cards */}
      <div className={styles.kpiGrid}>
        <Card className={styles.kpiCard}>
          <span className={styles.kpiValue}>{stats.total}</span>
          <span className={styles.kpiLabel}>Total de salas</span>
        </Card>
        <Card className={styles.kpiCard}>
          <span className={styles.kpiValue}>{stats.activas}</span>
          <span className={styles.kpiLabel}>Activas</span>
        </Card>
        <Card className={styles.kpiCard}>
          <span className={styles.kpiValue}>{stats.abiertas}</span>
          <span className={styles.kpiLabel}>Abiertas ahora</span>
        </Card>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <Input
            leftIcon={<Search size={16} />}
            placeholder="Buscar sala…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate('/config/salas/nueva')}
        >
          <Plus size={14} />
          Nueva sala
        </Button>
      </div>

      {/* Sala list */}
      {loading ? (
        <div className={styles.loadingText}>Cargando salas…</div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <Settings size={28} />
          <p>No hay salas configuradas. Cree una nueva sala para comenzar.</p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/config/salas/nueva')}
          >
            <Plus size={14} /> Crear primera sala
          </Button>
        </div>
      ) : (
        <div className={styles.salaList}>
          {filtered.map((sala) => {
            const open = isSalaOpen(sala);
            const prioridad = PRIORIDADES.find((p) => p.value === sala.prioridad);
            const tipoLabel = TIPOS_SALA.find((t) => t.value === sala.tipo)?.label ?? sala.tipo ?? '';

            return (
              <div
                key={sala.id}
                className={`${styles.salaRow} ${!sala.activa ? styles.salaInactiva : ''}`}
              >
                <div className={styles.salaMain}>
                  <div className={styles.salaIcon}>
                    <Stethoscope size={18} />
                  </div>
                  <div className={styles.salaInfo}>
                    <div className={styles.salaNameRow}>
                      <h3 className={styles.salaName}>{sala.nombre}</h3>
                      <div className={styles.salaBadges}>
                        <Badge variant={sala.activa ? 'success' : 'neutral'} size="sm">
                          {sala.activa ? 'Activa' : 'Inactiva'}
                        </Badge>
                        {sala.activa && (
                          <Badge variant={open ? 'success' : 'warning'} size="sm">
                            {open ? 'Abierta' : 'Cerrada'}
                          </Badge>
                        )}
                        {prioridad && prioridad.value !== 'normal' && (
                          <Badge variant={prioridad.variant} size="sm">
                            {prioridad.label}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {sala.descripcion && (
                      <p className={styles.salaDesc}>{sala.descripcion}</p>
                    )}
                    <div className={styles.salaMeta}>
                      {tipoLabel && (
                        <span className={styles.metaItem}>
                          <Stethoscope size={12} /> {tipoLabel}
                        </span>
                      )}
                      <span className={styles.metaItem}>
                        <Calendar size={12} />
                        {sala.usaHorariosV2 ? 'Horarios configurados' : 'Sin horarios'}
                      </span>
                      {sala.limitePacientes != null && (
                        <span className={styles.metaItem}>
                          <Users size={12} /> Máx {sala.limitePacientes}
                        </span>
                      )}
                      {sala.tiempoEstimadoMin != null && (
                        <span className={styles.metaItem}>
                          <Clock size={12} /> ~{sala.tiempoEstimadoMin} min/consulta
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.salaActions}>
                  <button
                    type="button"
                    className={`${styles.toggleBtn} ${sala.activa ? styles.toggleActive : ''}`}
                    onClick={() => handleToggleActiva(sala)}
                    title={sala.activa ? 'Desactivar sala' : 'Activar sala'}
                    role="switch"
                    aria-checked={sala.activa}
                  >
                    <span className={styles.toggleKnob} />
                  </button>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => navigate(`/config/salas/${sala.id}/editar`)}
                    title="Editar sala"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                    onClick={() => setDeleteModal({ id: sala.id, nombre: sala.nombre })}
                    title="Eliminar sala"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Delete confirmation ── */}
      <Modal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Eliminar sala"
      >
        <div className={styles.deleteBody}>
          <div className={styles.deleteIcon}>
            <AlertTriangle size={32} />
          </div>
          <p className={styles.deleteText}>
            ¿Está seguro de eliminar la sala <strong>{deleteModal?.nombre}</strong>?
            Esta acción no se puede deshacer.
          </p>
          <div className={styles.formActions}>
            <Button variant="secondary" onClick={() => setDeleteModal(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              <Trash2 size={14} /> Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
